<template>
    <div class="flex flex-col gap-2 w-full">
        <!-- The #empty slot only renders while the list is empty, so a region inside it would be
             created together with "Searching..."; this one stays mounted and only its text changes. -->
        <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ isFetching ? "Searching..." : "" }}</span>
        <BaseCombobox
            v-model="selectedItem"
            v-model:input="searchText"
            :input-id="inputId"
            :options="connectionItems"
            :placeholder="placeholder"
            :auto-open="autoOpen"
            :auto-focus="autoFocus"
            :actions="actions"
            @action="handleCreate">
            <template #empty>
                <div v-if="isFetching" class="flex gap-2 justify-center items-center p-2">
                    <LoadingSpinner class="w-4 h-4" aria-hidden="true" />
                    <p>Searching...</p>
                </div>
            </template>
            <template #action="{ action }">
                <div class="flex items-center gap-3 px-3 py-2">
                    <BaseAvatar size="sm" aria-hidden="true">
                        <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
                    </BaseAvatar>
                    <span>{{ action.label }}</span>
                </div>
            </template>
            <template #item="{ option }">
                <div class="flex items-center gap-3 px-3 py-2">
                    <PeopleAvatar size="sm" :name="option?.label" />
                    <span class="flex flex-col min-w-0">
                        <span class="body-small-semibold truncate">{{ option?.label }}</span>
                        <span v-if="option?.title" class="body-small opacity-75 truncate">{{ option.title }}</span>
                    </span>
                </div>
            </template>
        </BaseCombobox>
        <div v-if="showCancel" class="flex justify-end">
            <BaseButton theme="tertiary" size="sm" @click="emit('cancel')">Cancel</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox, { type ComboboxAction } from "~vue/components/BaseCombobox.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import type { Connection } from "~vue/types/connection";
import { computed, ref, watch } from "vue";

type ConnectionItem = { value: string; label: string; title?: string };

type ChatConnectionDisambiguationProps = {
    initialSearchTerm?: string;
    showCancel?: boolean;
    inputId?: string;
    placeholder?: string;
    autoOpen?: boolean;
    autoFocus?: boolean;
    excludeConnectionId?: string;
    allowCreate?: boolean;
};

const props = withDefaults(defineProps<ChatConnectionDisambiguationProps>(), {
    initialSearchTerm: "",
    showCancel: false,
    placeholder: "Search people...",
    autoOpen: true,
    autoFocus: false,
    excludeConnectionId: "",
    allowCreate: false,
});

const model = defineModel<Connection | null>({ default: null });

const emit = defineEmits<{
    select: [conn: Connection];
    create: [name: string];
    cancel: [];
}>();

const searchText = ref(props.initialSearchTerm);
const debouncedQuery = refDebounced(searchText, 300);
const searchUrl = computed(() => `/api/connections/?search=${encodeURIComponent(debouncedQuery.value)}`);

const { data, isFetching } = useFetch(searchUrl, { refetch: true }).json<Connection[]>();
const connections = computed(() => (data.value ?? []).filter((connection) => !props.excludeConnectionId || connection.id !== props.excludeConnectionId).slice(0, 10));

const connectionItems = computed<ConnectionItem[]>(() =>
    connections.value.map((connection) => ({
        value: connection.id,
        label: connection.persona.name ?? "(no name)",
        title: connection.job_title,
    })),
);

const selectedItem = ref<ConnectionItem | null>(null);

const actions = computed<ComboboxAction[]>(() => {
    const name = searchText.value.trim();
    if (!props.allowCreate || !name || isFetching.value) {
        return [];
    }
    return [{ id: "create", label: `Add “${name}” as a new person` }];
});

const handleCreate = (actionId: string) => {
    if (actionId !== "create") {
        return;
    }
    const name = searchText.value.trim();
    if (name) {
        emit("create", name);
    }
};

watch(selectedItem, (item) => {
    if (!item) {
        model.value = null;
        return;
    }
    const conn = connections.value.find((c) => c.id === item.value);
    if (conn) {
        model.value = conn;
        emit("select", conn);
    }
});
</script>
